import React, { Component, PropTypes } from 'react';
import partial from 'lodash/partial';
import classnames from 'classnames';
import { LoadingIndicator } from 'common/components/preloader';
import MakeDefault from 'account/containers/payout/make_acc_default_modal';
import DeleteModal from 'account/containers/payout/delete_acc_modal';
import PaypalModal from 'account/containers/payout/paypal_modal';
import PayoneerModal from 'account/components/payout/payoneer_modal';

/**
 * Payment Accounts.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class Accounts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      managing: false,
      makingDefaultAcc: null,
      deletingAcc: null,
      showingPaypal: false,
      showingPayoneer: false,
      addingPayoneer: false
    };

    this.toggleManage = this.toggleManage.bind(this);
    this.toggleMakeDefault = this.toggleMakeDefault.bind(this);
    this.toggleDelete = this.toggleDelete.bind(this);
    this.toggleEdit = this.toggleEdit.bind(this);
    this.togglePaypal = this.togglePaypal.bind(this);
    this.togglePayoneer = this.togglePayoneer.bind(this);
    this.onChange = this.onChange.bind(this);
    this.addAccount = this.addAccount.bind(this);
    this.addPayoneerAccount = this.addPayoneerAccount.bind(this);
  }

  componentDidMount() {
    this.props.fetch();
  }

  toggleEdit(system) {
    if (system === 'paypal') {
      this.togglePaypal();
    } else {
      this.togglePayoneer();
    }
  }

  toggleManage() {
    this.setState({ managing: !this.state.managing });
  }

  toggleMakeDefault(acc) {
    this.setState({ makingDefaultAcc: acc });
  }

  toggleDelete(acc) {
    this.setState({ deletingAcc: acc });
  }

  togglePaypal() {
    this.setState({ showingPaypal: !this.state.showingPaypal });
  }

  togglePayoneer() {
    this.setState({ showingPayoneer: !this.state.showingPayoneer });
  }

  onChange(accounts) {
    /* call `onChange` callback if provided, with updated list of accounts */
    if (this.props.onChange) {
      this.props.onChange(accounts);
    }
  }

  addPayoneerAccount() {
    /* create payoneer account and redirect user to registration link if provided */
    const { addPayoneerRequest, user, setBannerError, fetch } = this.props;
    this.setState({ addingPayoneer: true });
    return this.props.addPayoneerRequest(user.id)
      .then(({ data }) => {
        if (data.registration_link) {
          window.open(data.registration_link, '_blank');
        }
        /* fetch updated accounts list */
        fetch(true).then(({ data }) => this.onChange(data));
      })
      .catch(() => setBannerError('Failed to add Payoneer account', 'Please try again'))
      .then(() => this.setState({ addingPayoneer: false }))
  }

  addAccount(system) {
    /* if adding PayPal account, toggle modal so that paypal account info can be filled in */
    if (system === 'paypal') {
      this.togglePaypal();
    } else {
      this.addPayoneerAccount();
    }
  }

  renderAccount(account, system) {
    const { useIconsToConnect, showOnlyOneAccount } = this.props;
    const { managing, addingPayoneer } = this.state;
    const isPayoneer = system === 'payoneer';
    const connectDisabled = isPayoneer && addingPayoneer;
    const icon = (
      <i className={classnames("fa", { "fa-paypal": !isPayoneer, "fa-credit-card": isPayoneer })} />
    );

    if (!account) {
      return (
        <li className="connect">
          { useIconsToConnect?
          <div onClick={connectDisabled? undefined: () => this.addAccount(system)}>
            { connectDisabled? 'Please wait...' : icon }
            <h3>{ system }</h3>
          </div> :
          <button className="btn btn-md btn-secondary" disabled={connectDisabled}
              onClick={() => this.addAccount(system)}>
            { connectDisabled? 'Please wait...' : `Connect ${system}` }
          </button> }
        </li>);
    }

    return (
      <li key={system}>
        { icon }

        { isPayoneer?
        <span className="system">
          { account.status === 'enabled' ?
            'Payoneer account connected.' :
            'Payoneer account setup in process.' }
        </span> :
        <span className="system">{ account.email }</span> }

        { !showOnlyOneAccount && !managing && account.is_default &&
        <i className="fa fa-check-circle text-success"/> }

        { (managing || showOnlyOneAccount) &&
        <i className="fa fa-pencil" onClick={partial(this.toggleEdit, system)} /> }

        { (managing || showOnlyOneAccount) &&
        <i className="fa fa-trash" onClick={partial(this.toggleDelete, account)} /> }

        { !showOnlyOneAccount && managing && !account.is_default &&
        <button className="btn btn-text btn-sm" onClick={partial(this.toggleMakeDefault, account)}>
          Make default
        </button> }
      </li>);
  }

  render() {
    const { accounts, intro, showOnlyOneAccount } = this.props;
    const { managing, makingDefaultAcc, deletingAcc, showingPaypal, showingPayoneer } = this.state;
    const paypal = (accounts || []).find(acc => acc.system === 'paypal');
    const payoneer = (accounts || []).find(acc => acc.system === 'payoneer');

    return (
      <div className="payout-accounts">
        {accounts ?
        <div className="account-listing">
          <h4>Payment Accounts</h4>

          { intro || <p className="text-warning">
            If selecting Payoneer, be sure you have pop-ups enabled or it won't work.
          </p> }

          <ul>
            { (!showOnlyOneAccount || !payoneer) && this.renderAccount(paypal, 'paypal') }
            { (!showOnlyOneAccount || !paypal) && this.renderAccount(payoneer, 'payoneer') }
          </ul>

          { (paypal || payoneer) && !showOnlyOneAccount &&
          <button className="btn btn-text btn-md btn-manage" onClick={this.toggleManage}>
            { managing? 'Done' : 'Manage' }
          </button> }
        </div>:
        <LoadingIndicator fullScreen={false} />}

        <MakeDefault onToggle={partial(this.toggleMakeDefault, null)} account={makingDefaultAcc}
          onSuccess={partial(this.toggleMakeDefault, null)} />

        <DeleteModal onToggle={partial(this.toggleDelete, null)} account={deletingAcc}
          onSuccess={partial(this.toggleDelete, null)} />

        <PaypalModal onClose={this.togglePaypal} showing={showingPaypal} onDone={this.onChange}
          account={paypal} />

        <PayoneerModal onClose={this.togglePayoneer} showing={showingPayoneer} account={payoneer} />
      </div>);
  }
};

Accounts.propTypes = {
  user: PropTypes.object,
  accounts: PropTypes.array,
  fetch: PropTypes.func,
  addPayoneerRequest: PropTypes.func,
  setBannerError: PropTypes.func,
  onChange: PropTypes.func,
  intro: PropTypes.object,
  useIconsToConnect: PropTypes.bool,
  showOnlyOneAccount: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./account/components/payout/accounts.jsx